import { useClearRefinements, useNumericMenu } from 'react-instantsearch';
import { Button } from '@fpsm/cxy-components/Button';
import { RadioGroup, RadioGroupItem } from '@fpsm/cxy-components/RadioGroup';

import { type BettingInsightsResultAttribute, type EsIndex } from '~/apps/product-plus/configuration/types';

import { RangeSliderFilter } from '../RangeSliderFilter';

import styles from './styles/PriceSwingRefinement.module.css';

export type PriceSwingRefinementProps<TIndex extends EsIndex> = {
  attribute: BettingInsightsResultAttribute<TIndex>;
  isDisabled?: boolean;
  label: string;
};

export function PriceSwingRefinement<TIndex extends EsIndex>({
  attribute,
  isDisabled = false,
  label,
}: PriceSwingRefinementProps<TIndex>) {
  const valueAttribute = `${attribute}.swing.value`;
  const movementAttribute = `${attribute}.swingPercentage`;

  const { canRefine: canRefineClear, refine: clearRefinement } = useClearRefinements({
    includedAttributes: [valueAttribute, movementAttribute],
  });
  const {
    canRefine: canRefineMenu,
    items: menuItems,
    refine: refineMenu,
  } = useNumericMenu({
    attribute: movementAttribute,
    items: [
      { label: 'All', start: undefined, end: undefined },
      { label: 'Steamer', start: undefined, end: -0.01 },
      { label: 'Drifter', start: 0.01, end: undefined },
    ],
  });

  const getCurrentCategory = () => {
    const selectedItem = menuItems.find(item => item.isRefined);
    if (!selectedItem) return menuItems[0].value;

    return selectedItem.value;
  };

  return (
    <div className={styles.priceSwingRefinementContainer}>
      <div className={styles.priceSwingRadioGroupWrapper}>
        <RadioGroup
          aria-label={label}
          className={styles.radioGroup}
          disabled={isDisabled || !canRefineMenu}
          onValueChange={refineMenu}
          value={getCurrentCategory()}
        >
          {menuItems.map(({ label, value }) => (
            <RadioGroupItem className={styles.radioGroupItem} key={value} value={value}>
              {label}
            </RadioGroupItem>
          ))}
        </RadioGroup>
      </div>
      <RangeSliderFilter
        attribute={valueAttribute}
        data-analytics="minimumRuns"
        isDisabled={isDisabled}
        label={label}
        withInputs={true}
      />
      <Button
        className={styles.resetButton}
        disabled={isDisabled || !canRefineClear}
        onClick={clearRefinement}
        variant="ghost"
      >
        Reset
      </Button>
    </div>
  );
}
